/**
 * Commission de la boutique, par tranche de prix de vente — et par groupe de
 * déposant.
 *
 * Barème refait à zéro le 29/08/2026, quatre groupes avec un rôle chacun :
 *
 *   A : déposant normal (général) — 45 % jusqu'à 100 €, 40 % de 101 à 1 000 €,
 *       35 % de 1 001 à 2 000 €, 30 % au-delà
 *   B : marchand — 45 % jusqu'à 100 €, 35 % de 101 à 1 000 €, 30 % de 1 001
 *       à 2 500 €, 25 % au-delà
 *   C : personnel de la boutique — 20 % quel que soit le prix
 *   D : déposants "Troc en Stock" et "Troc en Stock achat" — 100 % quel que
 *       soit le prix (la marchandise appartient déjà à la boutique, aucun
 *       reversement)
 *
 * Les anciens groupes E et F (redondants avec C et D) ont été supprimés le
 * 29/08/2026.
 *
 * Le groupe se choisit sur la fiche du déposant ('A' par défaut) et s'applique
 * à tous ses articles, sauf taux négocié explicitement sur l'un d'eux — ce
 * taux négocié reste prioritaire, comme avant.
 */
/** Ordonné du plus cher au moins cher — la première tranche atteinte gagne. */
const BAREMES_COMMISSION = {
    A: [
        { minCentimes: 200_001, taux: 0.3 },
        { minCentimes: 100_001, taux: 0.35 },
        { minCentimes: 10_001, taux: 0.4 },
        { minCentimes: 0, taux: 0.45 },
    ],
    B: [
        { minCentimes: 250_001, taux: 0.25 },
        { minCentimes: 100_001, taux: 0.3 },
        { minCentimes: 10_001, taux: 0.35 },
        { minCentimes: 0, taux: 0.45 },
    ],
    C: [{ minCentimes: 0, taux: 0.2 }],
    D: [{ minCentimes: 0, taux: 1 }],
};
/** Groupe appliqué quand la fiche du déposant n'en précise pas. */
export const GROUPE_COMMISSION_DEFAUT = 'A';
/** La légende des groupes, pour l'afficher à côté du sélecteur. */
export const LIBELLE_GROUPE_COMMISSION = {
    A: '45 % jusqu’à 100 €, 40 % de 101 à 1 000 €, 35 % de 1 001 à 2 000 €, 30 % au-delà (déposant normal)',
    B: '45 % jusqu’à 100 €, 35 % de 101 à 1 000 €, 30 % de 1 001 à 2 500 €, 25 % au-delà (marchand)',
    C: '20 % quel que soit le prix (personnel)',
    D: '100 % — déposants Troc en Stock / Troc en Stock achat, sans reversement',
};
export const GROUPES_COMMISSION = ['A', 'B', 'C', 'D'];
/** Un code de groupe saisi ou lu en base, ramené à une valeur valide. */
export function groupeCommissionValide(valeur) {
    return valeur && GROUPES_COMMISSION.includes(valeur)
        ? valeur
        : GROUPE_COMMISSION_DEFAUT;
}
/** Taux de commission applicable à un prix de vente donné (en centimes), pour un groupe. */
export function tauxCommission(prixCentimes, groupe = GROUPE_COMMISSION_DEFAUT) {
    const bareme = BAREMES_COMMISSION[groupe] ?? BAREMES_COMMISSION[GROUPE_COMMISSION_DEFAUT];
    for (const tranche of bareme) {
        if (prixCentimes >= tranche.minCentimes)
            return tranche.taux;
    }
    // Inatteignable : la dernière tranche part de 0. Garde-fou explicite.
    return 0.45;
}
/**
 * Décompose un prix de vente en part boutique / part déposant.
 * L'arrondi se fait sur la commission, et le net déposant reçoit le reste :
 * commission + net est ainsi toujours exactement égal au prix encaissé.
 *
 * `tauxImpose` sert aux articles au taux négocié — la boutique en pratique
 * quelques-uns par mois. Le taux est figé sur l'article au moment du dépôt :
 * c'est le contrat signé qui fait foi, pas le barème du jour de la vente.
 * Sans taux imposé, c'est le barème du `groupe` du déposant qui s'applique.
 */
export function decomposerVente(prixCentimes, tauxImpose, groupe = GROUPE_COMMISSION_DEFAUT) {
    const taux = tauxImpose ?? tauxCommission(prixCentimes, groupe);
    const commissionCentimes = Math.round(prixCentimes * taux);
    return {
        prixCentimes,
        taux,
        commissionCentimes,
        netDeposantCentimes: prixCentimes - commissionCentimes,
    };
}
/** Un taux saisi en pourcent (« 20 », « 20 % », « 20,5 ») en fraction. */
export function tauxDepuisSaisie(saisie) {
    const nettoye = saisie.trim().replace('%', '').replace(',', '.').trim();
    if (nettoye === '')
        return null;
    const valeur = Number.parseFloat(nettoye);
    if (!Number.isFinite(valeur) || valeur < 0 || valeur > 100)
        return null;
    return Math.round(valeur * 100) / 10_000;
}
/** « 35 % » — pour l'affichage. */
export function tauxEnPourcent(taux) {
    return `${Math.round(taux * 100)} %`;
}
/** Phrase récapitulative du barème général, affichée à la saisie d'un dépôt. */
export const RESUME_BAREME = 'Commission : 45 % jusqu’à 100 €, 40 % de 101 à 1 000 €, 35 % de 1 001 à 2 000 €, 30 % au-delà.';
//# sourceMappingURL=commission.js.map